// sim/colony.ts — the Colony: a persistent root object that owns organisms.
//
// Colonies are FEW (not thousands), so the store is a small set of compact
// typed arrays keyed by ColonyId — same data-oriented discipline as the entity
// store, no per-colony heap objects. A colony persists across ticks: it holds a
// food stockpile (filled by Workers), a per-caste census, and a deterministic
// spawn cycle that turns stored food into new organisms (the autonomous growth
// loop). Spawning is the colony's job; how organisms BEHAVE lives in castes.ts.

import { KIND, CASTE, CASTE_COUNT, ORGANISM_STATE, NIL } from '../types.ts';
import type {
  EntityId,
  ColonyId,
  Caste,
  ColonyConfig,
  QueenConfig,
  OrganismState,
} from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { GenomeSystem } from './genome.ts';
import type { System, Tick } from '../types.ts';
import type { ReproductionGovernor } from './capacity.ts';

/**
 * The order in which a colony grows. Worker-heavy (food first), with a steady
 * trickle of soldiers, the occasional scout, and a rare specialist. Cycled by
 * each colony's spawnCursor, so growth is deterministic and identical per seed.
 */
const SPAWN_CYCLE: readonly Caste[] = [
  CASTE.Worker,
  CASTE.Worker,
  CASTE.Soldier,
  CASTE.Worker,
  CASTE.Flyer,
  CASTE.Worker,
  CASTE.Soldier,
  CASTE.Specialist,
];

/**
 * Default queen/persistence rules (used unless a builder passes its own). A
 * protected colony at ≤ 4 members recovers cheaply + fast; a lone queen raises a
 * free larva every 12 ticks. Tuned to keep a battered colony alive-but-weak.
 */
export const DEFAULT_QUEEN_CONFIG: QueenConfig = {
  criticalPopulation: 4,
  recoverySpawnCost: 2,
  recoveryCooldown: 8,
  emergencyCooldown: 12,
};

/**
 * GRACEFUL WEAKENING of an unrecoverable protected colony. A guarded colony that keeps
 * hitting pop 0 without ever climbing back over the critical line is in a death-spiral —
 * rather than flicker free larvae at a CONSTANT emergency cadence forever (an eternal
 * metronome), it raises them ever more SLOWLY as the stall deepens, so it reads as a
 * colony visibly languishing. A colony may flicker up to `STALL_RESCUE_GRACE` times at FULL
 * speed first — a rough patch is not yet "hopeless", and the grace keeps a normal struggling
 * start BYTE-IDENTICAL — then every further `STALL_RESCUE_STEP` rescues-without-escape
 * stretches the emergency cooldown by one more multiple, capped at `MAX_RESCUE_SLOWDOWN`×
 * (it NEVER stops — the anti-extinction guarantee still holds). The stall resets the instant
 * the colony climbs back out of recovery. Tuning constants — the single calibration point.
 */
export const STALL_RESCUE_GRACE = 12;
export const STALL_RESCUE_STEP = 6;
export const MAX_RESCUE_SLOWDOWN = 4;

// --- Queen ENTITY tuning (opt-in via ColonyConfig.queenEntity). These are TUNING
// SEAMS: the queen's hp pool and per-tick passive regen. UNTUNED placeholders — the
// threat curve that damages her lives in the world builder (ecosystemExample.ts),
// where it can be calibrated against real predation/aerial pressure. The queen never
// dies (damageQueen floors hp at 1); regen pulls her back toward QUEEN_MAX_HP when
// the siege lifts.
const QUEEN_MAX_HP = 300;
const QUEEN_REGEN = 2;

/** Behavioural state a freshly spawned member of each caste starts in. */
function initialState(caste: Caste): OrganismState {
  switch (caste) {
    case CASTE.Soldier:
      return ORGANISM_STATE.Patrolling;
    case CASTE.Flyer:
      return ORGANISM_STATE.Exploring;
    case CASTE.Specialist:
      return ORGANISM_STATE.Idle;
    default:
      return ORGANISM_STATE.SeekingFood; // Worker
  }
}

export class ColonySystem implements System {
  readonly name = 'colonies';

  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  /** Resolves each caste's current profile; spawns stamp the profile id. */
  private readonly genomes: GenomeSystem;
  private readonly config: ColonyConfig;

  readonly capacity: number;
  private colonyCount = 0;

  // --- Per-colony columns (compact SoA). ---
  private readonly homeX: Int32Array;
  private readonly homeY: Int32Array;
  private readonly foodStore: Int32Array;
  private readonly spawnCooldown: Int32Array;
  private readonly spawnCursor: Int32Array;
  private readonly population: Int32Array;
  /** Current generation of this colony — founders are gen 1; +1 each time the player advances it (a genome edit / evolution). Stamped onto each new spawn. */
  private readonly generation: Int32Array;
  private readonly aliveColony: Uint8Array;
  /** Per-caste census: index [colonyId * CASTE_COUNT + caste]. */
  private readonly casteCount: Int32Array;
  /**
   * Demand-spawn bias: a caste the colony AI wants the next cycle spawn to be,
   * or -1 for "follow the default SPAWN_CYCLE". Additive autonomy hook — with no
   * bias set (-1, the ctor default) growth is byte-identical to today.
   */
  private readonly spawnBias: Int32Array;
  /**
   * Rival kills CREDITED to each colony (its members slaying rival-colony
   * organisms). Drives the Predatory personality signal (sim/personality.ts).
   * Additive — incremented only at the kill chokepoint; with the personality
   * layer absent it is simply an unread counter.
   */
  private readonly killsBy: Int32Array;
  /**
   * Cumulative food DEPOSITED per colony (a returning Worker's haul). Monotonic
   * counter — the carrying-capacity layer samples its delta over a window to read
   * a colony's FOOD INCOME. Unread + not hashed when capacity is unwired, so the
   * base colony world stays byte-identical.
   */
  private readonly depositedBy: Int32Array;
  /**
   * Carrying-capacity reproduction governor (OPTIONAL, late-bound via
   * `attachReproduction`). When wired, the spawn loop reproduces against the
   * colony's DYNAMIC carrying capacity (faster below it, gated at it) instead of
   * the fixed `maxPopulation`. Absent ⇒ spawn behaviour is byte-identical to today.
   */
  private repro: ReproductionGovernor | undefined;
  /**
   * QUEEN PRESERVATION (opt-in via `protectColony`). A protected colony — the
   * player's — never goes extinct: it recovers instead. `recovering` mirrors that
   * live state (1 while at/below the critical population), purely for the census +
   * UI. Both 0 by default, so an unprotected colony is byte-identical to before.
   */
  private readonly protectedColony: Uint8Array;
  private readonly recovering: Uint8Array;
  /**
   * Death-spiral depth: consecutive pop-0 emergency rescues a guarded colony has taken
   * WITHOUT climbing back over the critical line. Stretches the emergency cadence (graceful
   * weakening) + feeds the languishing read-model; resets on escaping recovery. Always 0 for
   * an unguarded colony (the emergency path never runs) ⇒ off is byte-identical.
   */
  private readonly stallRescues: Int32Array;
  private readonly queen: QueenConfig;

  /**
   * QUEEN ENTITY (opt-in via `config.queenEntity`). When enabled, `spawnQueen` puts a
   * REAL immobile KIND.Queen entity at a colony's nest with its own HP pool. She is
   * intentionally NOT counted in population/casteCount (so "pop 0 = only the queen
   * remains" stays literally true) and NOT added to the spatial grid (she is render +
   * threat-only — never sensed, pathed, or fought). `queenEntity[cid]` holds her
   * EntityId (NIL = none). The small per-colony array is always allocated (maxColonies
   * ints) but only USED when `queenEnabled`, mirroring the larva-stage opt-in discipline.
   * Her hp is folded into the state hash automatically by the generic active-entity walk
   * (computeColonyStateHash), so NO separate queen hash fold exists.
   */
  private readonly queenEnabled: boolean;
  private readonly queenEntity: Int32Array;
  /**
   * OPTIONAL late-bound threat source (mirrors `attachReproduction`'s discipline). When
   * attached, each tick subtracts `queenThreat(cid)` from the colony's queen hp before
   * regen — the seam the ecosystem-pressure world wires to so sustained predation visibly
   * dips her HP. Absent ⇒ regen-only (she sits at full).
   */
  private queenThreat: ((cid: ColonyId) => number) | undefined;

  /**
   * LARVA LIFE-STAGE (opt-in via `config.larvaMaturation > 0`). When on, the
   * colony's autonomous reproductive stream LAYS larvae that develop for a fixed
   * number of ticks, then HATCH into adults (egg→larva→adult) — instead of the
   * adult appearing the instant food is spent. Founders, the lone-queen rescue,
   * and emergency recovery stay instant; only steady growth incubates. The queue
   * is a per-colony FIFO ring: since every larva shares the same maturation span,
   * lay order == hatch order, so we only ever check the head. `maturation === 0`
   * (the default) leaves the arrays unallocated and every path byte-identical.
   */
  private readonly larvaMaturation: number;
  /** Ring capacity per colony (0 when the stage is off). */
  private readonly larvaSlots: number;
  /** Absolute tick each pending larva hatches on. Index [cid*larvaSlots + slot]. */
  private readonly larvaHatchTick: Int32Array | undefined;
  /** Caste each pending larva will hatch as. Same indexing as larvaHatchTick. */
  private readonly larvaCaste: Uint8Array | undefined;
  /** Ring head (oldest pending larva) per colony. */
  private readonly larvaHead: Int32Array | undefined;
  /** Pending larva count per colony. */
  private readonly larvaCountCol: Int32Array | undefined;
  /** Larvae that have hatched into adults over the run (debug). */
  larvaeHatchedTotal = 0;

  /** Running totals (debug / behavioural assertions). */
  spawnedTotal = 0;
  depositedTotal = 0;
  /** Organisms lost across all colonies (starved or slain). */
  deathsTotal = 0;
  /** Colonies that have gone extinct (lost their last body, too poor to rebuild). */
  extinctTotal = 0;
  /** Times any colony advanced a generation (debug/hash). */
  generationsAdvancedTotal = 0;
  /** Free emergency larvae raised by lone queens (debug — the anti-extinction saves). */
  queenRescuesTotal = 0;

  constructor(
    entities: EntitySystem,
    grid: WorldGrid,
    genomes: GenomeSystem,
    config: ColonyConfig,
    maxColonies: number,
    queen: QueenConfig = DEFAULT_QUEEN_CONFIG,
  ) {
    if (maxColonies <= 0) throw new Error('ColonySystem maxColonies must be > 0');
    this.entities = entities;
    this.grid = grid;
    this.genomes = genomes;
    this.config = config;
    this.queen = queen;
    this.capacity = maxColonies;

    this.homeX = new Int32Array(maxColonies);
    this.homeY = new Int32Array(maxColonies);
    this.foodStore = new Int32Array(maxColonies);
    this.spawnCooldown = new Int32Array(maxColonies);
    this.spawnCursor = new Int32Array(maxColonies);
    this.population = new Int32Array(maxColonies);
    this.generation = new Int32Array(maxColonies);
    this.aliveColony = new Uint8Array(maxColonies);
    this.casteCount = new Int32Array(maxColonies * CASTE_COUNT);
    this.spawnBias = new Int32Array(maxColonies).fill(-1);
    this.killsBy = new Int32Array(maxColonies);
    this.depositedBy = new Int32Array(maxColonies);
    this.protectedColony = new Uint8Array(maxColonies);
    this.recovering = new Uint8Array(maxColonies);
    this.stallRescues = new Int32Array(maxColonies);

    // Queen entity (opt-in): the per-colony EntityId slot is always allocated (it is
    // tiny — maxColonies ints) but only READ/WRITTEN when queenEnabled, mirroring the
    // opt-in discipline. NIL = no queen entity for that colony.
    this.queenEnabled = config.queenEntity === true;
    this.queenEntity = new Int32Array(maxColonies).fill(NIL);

    // Larva life-stage: allocate the FIFO ring ONLY when the stage is enabled, so
    // an instant-spawn colony pays nothing (no memory, no per-tick work). The ring
    // is sized to maxPopulation — the autonomous lay path is gated below the cap, so
    // pending larvae can never exceed it.
    const mat = config.larvaMaturation ?? 0;
    this.larvaMaturation = mat > 0 ? mat : 0;
    if (this.larvaMaturation > 0) {
      this.larvaSlots = config.maxPopulation > 0 ? config.maxPopulation : 1;
      this.larvaHatchTick = new Int32Array(maxColonies * this.larvaSlots);
      this.larvaCaste = new Uint8Array(maxColonies * this.larvaSlots);
      this.larvaHead = new Int32Array(maxColonies);
      this.larvaCountCol = new Int32Array(maxColonies);
    } else {
      this.larvaSlots = 0;
      this.larvaHatchTick = undefined;
      this.larvaCaste = undefined;
      this.larvaHead = undefined;
      this.larvaCountCol = undefined;
    }
  }

  /**
   * Wire a carrying-capacity reproduction governor (the population-stability
   * foundation, src/sim/capacity.ts). Late-bound to break the construction cycle
   * (colony ← capacity ← colony). Optional — unset ⇒ the fixed-cap behaviour.
   */
  attachReproduction(governor: ReproductionGovernor): void {
    this.repro = governor;
  }

  /**
   * Mark a colony as QUEEN-PROTECTED (the player's): it can be ground down to a
   * single queen but never goes extinct — see `update` for the recovery rules.
   * Idempotent; call once during world setup for the player colony.
   */
  protectColony(cid: ColonyId): void {
    if (cid >= 0 && cid < this.capacity) this.protectedColony[cid] = 1;
  }

  // --- Queen entity (opt-in) -------------------------------------------------
  // A REAL immobile root unit (KIND.Queen) at the nest with its own HP. Owned end-to-end
  // by ColonySystem: spawn here, damage/regen in `update`. Deliberately NOT counted in
  // population/casteCount and NOT placed on the grid (render + threat-only).

  /**
   * Late-bind a threat source that damages each colony's queen per tick (mirrors
   * `attachReproduction`'s late-bound discipline). Optional — absent ⇒ regen-only.
   */
  attachQueenThreat(fn: (cid: ColonyId) => number): void {
    this.queenThreat = fn;
  }

  /** The queen entity's HP pool (her max / regen ceiling). */
  get queenMaxHp(): number {
    return QUEEN_MAX_HP;
  }

  /**
   * Spawn (idempotently) the colony's queen ENTITY at its nest. Returns her EntityId,
   * or NIL when the queen entity is disabled or `cid` is invalid. A plain `e.spawn`
   * (no RNG, no grid.add) so she perturbs no randomness and is never spatially sensed.
   * Call once during world setup for a PROTECTED colony.
   */
  spawnQueen(cid: ColonyId): EntityId {
    if (!this.queenEnabled || cid < 0 || cid >= this.colonyCount) return NIL;
    if (this.queenEntity[cid] !== NIL) return this.queenEntity[cid] as number; // idempotent
    const e = this.entities;
    const id = e.spawn(KIND.Queen, this.homeX[cid] as number, this.homeY[cid] as number);
    e.colony[id] = cid;
    e.hp[id] = QUEEN_MAX_HP;
    // blocks defaults to 0 for non-Ant kinds (she occupies no tile); leave it.
    // Do NOT call grid.add(id): the queen is render/threat-only, never spatially sensed.
    this.queenEntity[cid] = id;
    return id;
  }

  /**
   * Deal `amount` damage to a colony's queen (public seam for the threat source / tests).
   * She NEVER dies — her hp floors at 1. No-op when the queen entity is disabled, absent,
   * or `amount <= 0`.
   */
  damageQueen(cid: ColonyId, amount: number): void {
    if (!this.queenEnabled || cid < 0 || cid >= this.capacity || amount <= 0) return;
    const id = this.queenEntity[cid] as number;
    if (id === NIL) return;
    const hp = this.entities.hp[id] as number;
    this.entities.hp[id] = Math.max(1, hp - amount);
  }

  /** The colony's queen EntityId, or NIL when none. */
  queenEntityAt(cid: ColonyId): EntityId {
    if (cid < 0 || cid >= this.capacity) return NIL;
    return this.queenEntity[cid] as number;
  }
  /** The colony's queen HP (0 when she has no entity). */
  queenHpAt(cid: ColonyId): number {
    if (cid < 0 || cid >= this.capacity) return 0;
    const id = this.queenEntity[cid] as number;
    return id !== NIL ? (this.entities.hp[id] as number) : 0;
  }
  /** True while the colony has a live queen entity. */
  queenAlive(cid: ColonyId): boolean {
    if (cid < 0 || cid >= this.capacity) return false;
    const id = this.queenEntity[cid] as number;
    return id !== NIL && (this.entities.alive[id] as number) === 1;
  }
  /** True when the queen is alive AND below a quarter of her HP (integer-safe). */
  isQueenImperiled(cid: ColonyId): boolean {
    return this.queenAlive(cid) && this.queenHpAt(cid) * 4 < QUEEN_MAX_HP;
  }

  // --- Lifecycle -------------------------------------------------------------

  /** Found a colony with its nest at (x,y), seeded with the configured food. */
  createColony(x: number, y: number): ColonyId {
    if (this.colonyCount >= this.capacity) {
      throw new Error(`ColonySystem capacity ${this.capacity} exhausted`);
    }
    const id = this.colonyCount++;
    this.homeX[id] = x;
    this.homeY[id] = y;
    this.foodStore[id] = this.config.initialFood;
    this.spawnCooldown[id] = 0;
    this.spawnCursor[id] = 0;
    this.population[id] = 0;
    this.generation[id] = 1;
    this.aliveColony[id] = 1;
    return id;
  }

  /**
   * Spawn one organism of `caste` at (x,y) for colony `cid`, fully initialised
   * from the caste config and placed on the grid. O(1), allocation-free. This
   * is the "spawn new organisms from colony" primitive.
   */
  spawnOrganism(cid: ColonyId, caste: Caste, x: number, y: number): EntityId {
    const e = this.entities;
    // Capture the caste's CURRENT genome profile; later edits won't touch this
    // unit (non-retroactive). The unit carries its genetics via profileRef.
    const pid = this.genomes.currentProfileId(caste);
    const p = this.genomes.profile(pid);
    const id = e.spawn(KIND.Organism, x, y);
    e.caste[id] = caste;
    e.colony[id] = cid;
    e.energy[id] = p.startEnergy;
    e.hp[id] = p.startHp;
    e.cargo[id] = 0;
    e.profileRef[id] = pid;
    e.generation[id] = this.generation[cid] as number;
    // Flyers — and any gene-granted flyer — don't occupy tiles.
    e.blocks[id] = p.flying ? 0 : 1;
    e.targetX[id] = this.homeX[cid] as number;
    e.targetY[id] = this.homeY[cid] as number;
    e.state[id] = initialState(caste);
    this.grid.add(id);

    this.population[cid] = (this.population[cid] as number) + 1;
    this.casteCount[cid * CASTE_COUNT + caste] =
      (this.casteCount[cid * CASTE_COUNT + caste] as number) + 1;
    this.spawnedTotal++;
    return id;
  }

  /**
   * Spawn the next member at this colony's nest. If the AI has set a spawn bias
   * (>= 0) that caste is produced; otherwise the default SPAWN_CYCLE is followed.
   * The cursor ADVANCES either way, so the fallback cycle stays deterministic
   * regardless of whether a bias was in effect — when no bias is ever set this is
   * byte-identical to the original behaviour.
   */
  private spawnFromCycle(cid: ColonyId): EntityId {
    return this.spawnOrganism(
      cid,
      this.nextCycleCaste(cid),
      this.homeX[cid] as number,
      this.homeY[cid] as number,
    );
  }

  /**
   * Advance this colony's spawn cursor and return the caste it selects — the AI's
   * spawn bias when set (>= 0), otherwise the next SPAWN_CYCLE step. The cursor
   * advances either way, so the fallback cycle stays deterministic whether or not a
   * bias was in effect (byte-identical to the original inline selection). Shared by
   * both the instant spawn (`spawnFromCycle`) and the larva lay (`layFromCycle`).
   */
  private nextCycleCaste(cid: ColonyId): Caste {
    const cursor = this.spawnCursor[cid] as number;
    const biased = this.spawnBias[cid] as number;
    const caste = (biased >= 0
      ? biased
      : (SPAWN_CYCLE[cursor % SPAWN_CYCLE.length] as number)) as Caste;
    this.spawnCursor[cid] = (cursor + 1) % SPAWN_CYCLE.length;
    return caste;
  }

  /**
   * Lay one larva of the next cycle caste (the opt-in growth path). Picks the caste
   * exactly as `spawnFromCycle` would (same cursor advance), then queues it to hatch
   * `larvaMaturation` ticks from now instead of producing an adult immediately.
   */
  private layFromCycle(cid: ColonyId, tick: Tick): void {
    this.layLarva(cid, this.nextCycleCaste(cid), tick);
  }

  /**
   * Enqueue a developing larva of `caste` for colony `cid`, hatching at
   * `tick + larvaMaturation`. FIFO ring; a full ring (only reachable past the
   * population cap) drops the lay — the cooldown still applies, so it retries.
   */
  private layLarva(cid: ColonyId, caste: Caste, tick: Tick): void {
    const head = this.larvaHead as Int32Array;
    const count = this.larvaCountCol as Int32Array;
    const hatch = this.larvaHatchTick as Int32Array;
    const cst = this.larvaCaste as Uint8Array;
    const n = count[cid] as number;
    if (n >= this.larvaSlots) return; // ring full (shouldn't happen under the cap gate)
    const slot = ((head[cid] as number) + n) % this.larvaSlots;
    const idx = cid * this.larvaSlots + slot;
    hatch[idx] = (tick as number) + this.larvaMaturation;
    cst[idx] = caste;
    count[cid] = n + 1;
  }

  /**
   * Hatch every larva of colony `cid` whose maturation has elapsed (hatchTick <=
   * tick), oldest first, into real adults via `spawnOrganism`. Because all larvae
   * share one maturation span the queue is monotonic, so we stop at the first head
   * that is not yet due. O(hatched), allocation-free.
   */
  private hatchColony(cid: ColonyId, tick: Tick): void {
    const head = this.larvaHead as Int32Array;
    const count = this.larvaCountCol as Int32Array;
    const hatch = this.larvaHatchTick as Int32Array;
    const cst = this.larvaCaste as Uint8Array;
    const t = tick as number;
    while ((count[cid] as number) > 0) {
      const slot = head[cid] as number;
      const idx = cid * this.larvaSlots + slot;
      if ((hatch[idx] as number) > t) break; // earliest larva still developing
      const caste = cst[idx] as Caste;
      head[cid] = (slot + 1) % this.larvaSlots;
      count[cid] = (count[cid] as number) - 1;
      this.spawnOrganism(cid, caste, this.homeX[cid] as number, this.homeY[cid] as number);
      this.larvaeHatchedTotal++;
    }
  }

  /**
   * Seed `count` founding members at the nest (no food cost — bootstrap stock).
   * Follows the same growth cycle as autonomous spawning, so the starting mix is
   * worker-heavy and deterministic. Call during world setup.
   */
  seedInitialMembers(cid: ColonyId, count: number): void {
    for (let i = 0; i < count; i++) this.spawnFromCycle(cid);
  }

  /** Record an organism's death: drop it from its colony's census. */
  onDeath(cid: ColonyId, caste: Caste): void {
    if (cid < 0) return;
    const p = this.population[cid] as number;
    if (p > 0) this.population[cid] = p - 1;
    const k = cid * CASTE_COUNT + caste;
    const c = this.casteCount[k] as number;
    if (c > 0) this.casteCount[k] = c - 1;
    this.deathsTotal++;
  }

  /** Add gathered food to a colony's stockpile (a returning Worker depositing). */
  depositFood(cid: ColonyId, amount: number): void {
    if (cid < 0 || amount <= 0) return;
    this.foodStore[cid] = (this.foodStore[cid] as number) + amount;
    this.depositedBy[cid] = (this.depositedBy[cid] as number) + amount;
    this.depositedTotal += amount;
  }

  /**
   * Shorten a colony's pending spawn cooldown by up to `ticks` (never below 0),
   * returning the amount actually shaved. The seam a tending NURSE pulls — a
   * Worker stationed at the Nursery hurries the next brood along. Additive: with
   * no Nurse task ever posted the cooldown clock is byte-identical to today.
   */
  hurryBrood(cid: ColonyId, ticks: number): number {
    if (cid < 0 || ticks <= 0) return 0;
    const cd = this.spawnCooldown[cid] as number;
    if (cd <= 0) return 0;
    const shave = ticks < cd ? ticks : cd;
    this.spawnCooldown[cid] = cd - shave;
    return shave;
  }

  /**
   * Remove up to `amount` food from a colony's stockpile, returning what was
   * actually taken (bounded by what's there, never negative). Used by the
   * ecosystem-pressure layer when a Rat presence robs the larder. Additive seam —
   * nothing in the core sim calls it.
   */
  stealFood(cid: ColonyId, amount: number): number {
    if (cid < 0 || amount <= 0) return 0;
    const have = this.foodStore[cid] as number;
    const take = amount < have ? amount : have;
    if (take > 0) this.foodStore[cid] = have - take;
    return take;
  }

  /**
   * Withdraw `amount` food from a colony's stockpile if it can afford the full sum.
   * Returns true when spent (the store never goes negative), false otherwise. The
   * seam for systems that CONSUME colony resources — e.g. region-scale projects
   * paying down their build cost. Spawn food is handled separately in `update`.
   */
  spendFood(cid: ColonyId, amount: number): boolean {
    if (cid < 0 || amount <= 0) return false;
    const have = this.foodStore[cid] as number;
    if (have < amount) return false;
    this.foodStore[cid] = have - amount;
    return true;
  }

  // --- Per-tick: turn stored food into new organisms ------------------------

  update(tick: Tick): void {
    const cfg = this.config;
    const q = this.queen;
    const incubating = this.larvaMaturation > 0;
    for (let cid = 0; cid < this.colonyCount; cid++) {
      if (this.aliveColony[cid] === 0) continue;
      // QUEEN ENTITY tick (opt-in): apply this tick's threat (if a source is attached),
      // then passively regen toward QUEEN_MAX_HP. Runs EVERY tick for the colony,
      // independent of the spawn cooldown / larva logic below (so her HP keeps moving
      // even when the colony is mid-cooldown). She NEVER dies (damageQueen floors at 1).
      // No-op + no allocation when the queen entity is disabled.
      if (this.queenEnabled && this.queenEntity[cid] !== NIL) {
        if (this.queenThreat) this.damageQueen(cid, this.queenThreat(cid));
        const id = this.queenEntity[cid] as number;
        const hp = this.entities.hp[id] as number;
        if (hp < QUEEN_MAX_HP) {
          this.entities.hp[id] = Math.min(QUEEN_MAX_HP, hp + QUEEN_REGEN);
        }
      }
      // Develop the brood FIRST: any larva whose maturation has elapsed becomes an
      // adult this tick, independent of the spawn cooldown (which gates LAYING, not
      // hatching). No-op — and the ring stays unallocated — when the stage is off.
      if (incubating) this.hatchColony(cid, tick);
      const cd = this.spawnCooldown[cid] as number;
      if (cd > 0) {
        this.spawnCooldown[cid] = cd - 1;
        continue;
      }
      const pop = this.population[cid] as number;
      const food = this.foodStore[cid] as number;
      // Developing larvae count as future bodies: they hold a slot under the cap and
      // keep a battered colony from being declared extinct. Always 0 when off.
      const pending = incubating ? (this.larvaCountCol as Int32Array)[cid] as number : 0;
      const guarded = this.protectedColony[cid] === 1;

      // QUEEN-PROTECTED colonies (the player's) experience setbacks, never accidental
      // extinction. `recovering` is the live "in trouble" flag for the census/UI.
      const inRecovery = guarded && pop <= q.criticalPopulation;
      this.recovering[cid] = inRecovery ? 1 : 0;
      // Escaping recovery (back over the critical line) clears the death-spiral counter so a
      // colony that genuinely rebuilt rescues at full speed again. No-op for an unguarded
      // colony (always 0), keeping the off-path byte-identical.
      if (!inRecovery) this.stallRescues[cid] = 0;

      // ONLY THE QUEEN REMAINS (pop 0). She cannot die: raise FREE emergency larvae
      // (worker-biased, no food required) so the colony always claws back from zero.
      // This pre-empts the extinction check below, so a guarded colony can never
      // silently disappear. GRACEFUL WEAKENING: a colony stuck here (no net growth) stretches
      // its cadence as the stall deepens — a slowing flicker, never a stop (capped slowdown).
      if (guarded && pop === 0) {
        this.spawnOrganism(cid, CASTE.Worker, this.homeX[cid] as number, this.homeY[cid] as number);
        // A rough patch (≤ GRACE rescues) runs at FULL speed — byte-identical to a no-stall
        // world; only a GENUINELY eternal flicker (beyond the grace) gradually slows, capped.
        const stall = this.stallRescues[cid] as number;
        const over = stall > STALL_RESCUE_GRACE ? stall - STALL_RESCUE_GRACE : 0;
        const slow = 1 + Math.min((over / STALL_RESCUE_STEP) | 0, MAX_RESCUE_SLOWDOWN - 1);
        this.spawnCooldown[cid] = q.emergencyCooldown * slow;
        this.stallRescues[cid] = stall + 1;
        this.queenRescuesTotal++;
        continue;
      }

      // Honest extinction — UNGUARDED colonies only: no bodies, NO developing brood,
      // AND too little food to ever raise another ⇒ gone. (A guarded pop-0 colony was
      // handled above; a pop-0 colony that can still afford a spawn — or still has a
      // larva incubating — is merely REBUILDING. `pending` is always 0 when the stage
      // is off, so this stays byte-identical.)
      if (pop === 0 && pending === 0 && food < cfg.spawnCost) {
        this.aliveColony[cid] = 0;
        this.extinctTotal++;
        continue;
      }
      // EMERGENCY RECOVERY: a battered protected colony rebuilds with REPRODUCTION UP
      // (cheap spawn, paid down to free) + FAST cadence + WORKER PRIORITY — BEFORE any
      // capacity/food gate, so a weak colony always claws back. Expansion pauses (its
      // tiny pop is far below the AI's expand threshold) and MILITARY pauses (we force
      // Worker, ignoring any Soldier spawn-bias). It grows weak-but-steady until it
      // climbs back over the critical line. (A recovering colony sits far below the
      // carrying-capacity gate, so the two governors never contend.)
      if (inRecovery) {
        const cost = food < q.recoverySpawnCost ? food : q.recoverySpawnCost;
        this.foodStore[cid] = food - cost;
        this.spawnOrganism(cid, CASTE.Worker, this.homeX[cid] as number, this.homeY[cid] as number);
        this.spawnCooldown[cid] = q.recoveryCooldown;
        continue;
      }

      // Reproduce against CARRYING CAPACITY when a governor is wired (dynamic,
      // food/territory-driven), else the fixed maxPopulation. The dynamic value is
      // already clamped ≤ maxPopulation (the hard entity-allocation bound), so it
      // never exceeds the cap; here we just never spawn AT or ABOVE it.
      const reproCap = this.repro
        ? Math.min(this.repro.capacityOf(cid), cfg.maxPopulation)
        : cfg.maxPopulation;
      // Pending larvae already claim a slot, so the live pop PLUS incubating brood is
      // what the cap gates — otherwise a colony would over-lay while larvae develop
      // and then overshoot when they all hatch. `pending` is 0 when the stage is off.
      if (pop + pending >= reproCap) continue;
      if (food < cfg.spawnCost) continue;
      this.foodStore[cid] = food - cfg.spawnCost;
      // Lay a developing larva (hatches `larvaMaturation` ticks later) when the stage
      // is on; otherwise the adult appears immediately, exactly as before.
      if (incubating) this.layFromCycle(cid, tick);
      else this.spawnFromCycle(cid);
      // Below capacity ⇒ a shorter cooldown (INCREASED reproduction); near capacity
      // ⇒ the normal cooldown (STABLE). Governor absent ⇒ the base cooldown.
      let nextCd = cfg.spawnCooldown;
      if (this.repro) {
        const scaled = Math.floor((nextCd * this.repro.cooldownScaleOf(cid)) / 1000);
        nextCd = scaled < 1 ? 1 : scaled;
      }
      this.spawnCooldown[cid] = nextCd;
    }
  }

  // --- Accessors (behaviours, triggers, stats, hashing) ----------------------

  get count(): number {
    return this.colonyCount;
  }
  homeXAt(cid: ColonyId): number {
    return this.homeX[cid] as number;
  }
  homeYAt(cid: ColonyId): number {
    return this.homeY[cid] as number;
  }
  /** Nest X (the colony spawn point). Read-only alias of homeXAt — the apex layer
   *  reads it to AVOID camping nests; never mutates colony state. */
  nestX(cid: ColonyId): number {
    return this.homeX[cid] as number;
  }
  /** Nest Y (the colony spawn point). Read-only alias of homeYAt (see nestX). */
  nestY(cid: ColonyId): number {
    return this.homeY[cid] as number;
  }
  foodAt(cid: ColonyId): number {
    return this.foodStore[cid] as number;
  }
  populationAt(cid: ColonyId): number {
    return this.population[cid] as number;
  }
  /** Advance this colony to its next generation (a player evolution). Future spawns inherit it; existing organisms keep theirs. Returns the new generation. */
  advanceGeneration(cid: ColonyId): number { if (cid < 0) return 0; const g = (this.generation[cid] as number) + 1; this.generation[cid] = g; this.generationsAdvancedTotal++; return g; }
  /** This colony's current generation (founders = 1). */
  generationAt(cid: ColonyId): number { return this.generation[cid] as number; }
  /** True while the colony root still stands (has not gone extinct). */
  isAlive(cid: ColonyId): boolean {
    return this.aliveColony[cid] === 1;
  }
  /** True once the colony has lost its last body and cannot rebuild — gone for good. */
  isExtinct(cid: ColonyId): boolean {
    return !this.isAlive(cid);
  }
  /** True if this colony is queen-protected (the player's — cannot go extinct). */
  isProtected(cid: ColonyId): boolean {
    return this.protectedColony[cid] === 1;
  }
  /** True while a protected colony is in emergency recovery (at/below critical pop). */
  isRecovering(cid: ColonyId): boolean {
    return this.recovering[cid] === 1;
  }
  /**
   * Death-spiral depth: consecutive pop-0 emergency rescues without escaping recovery (0 when
   * healthy/growing). Climbs as a hopeless colony stalls; the emergency cadence stretches with
   * it. A read-model for the UI to surface a "your colony is languishing" prompt past a threshold.
   */
  rescueStallOf(cid: ColonyId): number {
    return this.stallRescues[cid] as number;
  }
  /**
   * Brood-READY count: how many larvae the colony could afford to raise RIGHT NOW —
   * bounded by both headroom under the population cap and what its food can buy. This
   * is the CAPACITY-to-grow signal the diagnostics cascade reads (brood == 0 ⇒ the
   * colony is larva-limited); it is NOT the count of larvae actually developing. When
   * the larva life-stage is active, `larvaPendingAt` is the live developing count and
   * `larvaCensusAt` is what the UI shows as "Larvae".
   */
  broodReadyAt(cid: ColonyId): number {
    const cfg = this.config;
    // Headroom is under the colony's CARRYING CAPACITY when wired (so the larvae
    // count reflects the real, dynamic ceiling), else the fixed cap.
    const ceiling = this.repro
      ? Math.min(this.repro.capacityOf(cid), cfg.maxPopulation)
      : cfg.maxPopulation;
    const room = ceiling - (this.population[cid] as number);
    const afford = Math.floor((this.foodStore[cid] as number) / cfg.spawnCost);
    const v = Math.min(room, afford);
    return v < 0 ? 0 : v;
  }
  /** Maturation span of a larva in ticks (0 ⇒ the life-stage is off / instant spawn). */
  get larvaMaturationTicks(): number {
    return this.larvaMaturation;
  }
  /** Larvae CURRENTLY developing in colony `cid` (0 when the life-stage is off). */
  larvaPendingAt(cid: ColonyId): number {
    return this.larvaCountCol ? (this.larvaCountCol[cid] as number) : 0;
  }
  /**
   * The i-th pending larva's hatch tick (FIFO from the oldest), for state hashing.
   * Returns 0 when the stage is off or `i` is out of range.
   */
  larvaHatchTickAt(cid: ColonyId, i: number): number {
    const count = this.larvaCountCol;
    if (!count || i < 0 || i >= (count[cid] as number)) return 0;
    const slot = ((this.larvaHead as Int32Array)[cid] as number + i) % this.larvaSlots;
    return (this.larvaHatchTick as Int32Array)[cid * this.larvaSlots + slot] as number;
  }
  /** The i-th pending larva's caste (FIFO from the oldest), for state hashing. */
  larvaCasteAt(cid: ColonyId, i: number): number {
    const count = this.larvaCountCol;
    if (!count || i < 0 || i >= (count[cid] as number)) return 0;
    const slot = ((this.larvaHead as Int32Array)[cid] as number + i) % this.larvaSlots;
    return (this.larvaCaste as Uint8Array)[cid * this.larvaSlots + slot] as number;
  }
  /**
   * What the UI shows as "Larvae": the REAL developing-larva count when the
   * life-stage is on, else the derived brood-ready estimate (`broodReadyAt`). Keeps
   * the census meaningful in both modes from a single call site.
   */
  larvaCensusAt(cid: ColonyId): number {
    return this.larvaMaturation > 0 ? this.larvaPendingAt(cid) : this.broodReadyAt(cid);
  }
  casteCountAt(cid: ColonyId, caste: Caste): number {
    return this.casteCount[cid * CASTE_COUNT + caste] as number;
  }
  /** Cumulative food deposited into this colony (the income-window watermark). */
  depositedByAt(cid: ColonyId): number {
    return this.depositedBy[cid] as number;
  }
  spawnCursorAt(cid: ColonyId): number {
    return this.spawnCursor[cid] as number;
  }
  spawnCooldownAt(cid: ColonyId): number {
    return this.spawnCooldown[cid] as number;
  }

  /**
   * Set the demand-spawn bias for `cid`: the next cycle spawns become `caste`
   * (a CASTE.* value), or pass -1 to clear and resume the default SPAWN_CYCLE.
   * The autonomy layer (ColonyAISystem) calls this; nothing else does.
   */
  setSpawnBias(cid: ColonyId, caste: number): void {
    this.spawnBias[cid] = caste;
  }
  /** Current demand-spawn bias for `cid` (-1 = default cycle). */
  spawnBiasAt(cid: ColonyId): number {
    return this.spawnBias[cid] as number;
  }

  /**
   * Credit `cid` with one rival kill (called at the kill chokepoint when one of
   * its members slays a rival-colony organism). Guards a negative/NIL colony so
   * unattributed kills are no-ops. Feeds the Predatory personality signal.
   */
  recordKill(cid: ColonyId): void {
    if (cid < 0) return;
    this.killsBy[cid] = (this.killsBy[cid] as number) + 1;
  }
  /** Total rival kills credited to `cid` over the run. */
  killsByAt(cid: ColonyId): number {
    return this.killsBy[cid] as number;
  }
  /** Food level below which this colony's Specialists wake up. */
  get lowFoodThreshold(): number {
    return this.config.lowFoodThreshold;
  }

  /** Sum of all colony food stockpiles (demo stat). */
  totalFood(): number {
    let t = 0;
    for (let i = 0; i < this.colonyCount; i++) t += this.foodStore[i] as number;
    return t;
  }
  /** Sum of all live colony populations (demo stat). */
  totalPopulation(): number {
    let t = 0;
    for (let i = 0; i < this.colonyCount; i++) t += this.population[i] as number;
    return t;
  }
}
